/**
 * DashboardSkeleton — full-screen auth-loading skeleton.
 *
 * Used by ProtectedRoute / RequireClient while Firebase auth resolves
 * and /api/auth/me + /api/clients are in flight. Renders BEFORE AppShell
 * mounts, so it has to include its own chrome (sidebar + mobile top
 * strip) — once auth resolves AppShell takes over and looks the same,
 * so the visual hand-off is invisible.
 *
 * Mirrors the new AppShell (Phase 1): w-60 desktop sidebar with white
 * surface + brand wordmark area + 5 nav-item placeholders + Phase 1
 * Skeleton primitives (opacity-tiered ambient/prominent placeholders).
 * Mobile: top brand strip + bottom tab bar matching AppShell's grid-cols-5.
 *
 * Page-internal loading (when stats are in flight while AppShell is
 * already mounted) is handled inline inside DashboardPage with section-
 * level skeletons, NOT this component.
 */

import { SkeletonBox } from './Skeleton';

export function DashboardSkeleton() {
  return (
    <div className="flex min-h-screen flex-col bg-bg-tint">
      {/* Mobile top brand strip — mirrors AppShell mobile header */}
      <header className="sticky top-0 z-30 border-b border-dark/5 bg-white md:hidden">
        <div className="flex h-14 items-center justify-between px-4">
          <SkeletonBox tone="prominent" className="h-5 w-28" />
          <SkeletonBox className="h-3 w-12" />
        </div>
      </header>

      <div className="md:flex md:min-h-screen">
        {/* Desktop sidebar — w-60 to match AppShell exactly */}
        <aside className="hidden md:sticky md:top-0 md:flex md:h-screen md:w-60 md:shrink-0 md:flex-col md:border-r md:border-dark/5 md:bg-white">
          <div className="flex h-16 items-center border-b border-dark/5 px-6">
            <SkeletonBox tone="prominent" className="h-6 w-32" />
          </div>
          <nav className="flex-1 space-y-1 px-3 py-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="flex items-center gap-3 rounded-lg px-3 py-2.5"
              >
                <SkeletonBox tone="prominent" className="h-5 w-5 shrink-0" />
                <SkeletonBox className="h-4 w-24" />
              </div>
            ))}
          </nav>
          <div className="border-t border-dark/5 px-6 py-4">
            <SkeletonBox className="h-3 w-16" />
          </div>
        </aside>

        {/* Main content area — section-matching skeleton (greeting +
            banners + hero + free plan + 4 platform cards + recent activity).
            Lifts from states.astro "Home Loading" mockup. */}
        <main className="min-w-0 flex-1 pb-20 md:pb-0">
          <div
            aria-busy="true"
            aria-live="polite"
            className="mx-auto max-w-6xl space-y-6 px-4 py-6 sm:px-6 lg:px-8 lg:py-10"
          >
            {/* Greeting */}
            <div>
              <SkeletonBox tone="prominent" className="h-10 w-64 sm:h-12" />
              <SkeletonBox className="mt-3 h-4 w-32" />
            </div>

            {/* Banner skeletons */}
            <div className="space-y-2">
              <SkeletonBox className="h-16 w-full rounded-2xl" />
              <SkeletonBox className="h-16 w-full rounded-2xl" />
            </div>

            {/* Hero */}
            <section className="rounded-3xl border border-dark/5 bg-card p-6 shadow-card sm:p-8 lg:p-10">
              <SkeletonBox className="h-3 w-24" />
              <SkeletonBox
                tone="prominent"
                className="mt-3 h-16 w-48 rounded-lg sm:h-20"
              />
              <SkeletonBox className="mt-3 h-6 w-96 max-w-full" />
              <SkeletonBox className="mt-4 h-4 w-64" />
            </section>

            {/* Free plan */}
            <section className="rounded-3xl border border-dark/5 bg-card p-5 shadow-card sm:p-6">
              <div className="mb-3 flex items-center justify-between">
                <SkeletonBox className="h-3 w-28" />
                <SkeletonBox className="h-3 w-36" />
              </div>
              <SkeletonBox className="h-2 w-full rounded-full" />
              <SkeletonBox className="mt-3 h-4 w-72 max-w-full" />
            </section>

            {/* Section H2 */}
            <SkeletonBox className="h-4 w-48 px-1" />

            {/* 4 per-platform cards */}
            <div className="space-y-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className="rounded-3xl border border-dark/5 bg-card p-5 shadow-card sm:p-6"
                >
                  <div className="mb-4 flex items-center gap-3">
                    <SkeletonBox tone="prominent" className="h-8 w-8" />
                    <SkeletonBox tone="prominent" className="h-5 w-32 flex-1" />
                    <SkeletonBox className="h-5 w-16 rounded-full bg-green-100" />
                  </div>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6">
                    <div>
                      <SkeletonBox className="mb-2 h-3 w-20" />
                      <SkeletonBox tone="prominent" className="h-8 w-16" />
                      <SkeletonBox className="mt-2 h-3 w-24" />
                    </div>
                    <div className="sm:border-l sm:border-dark/5 sm:pl-6">
                      <SkeletonBox className="mb-2 h-3 w-32" />
                      <SkeletonBox tone="prominent" className="h-8 w-24" />
                      <SkeletonBox className="mt-2 h-3 w-40" />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Recent activity table */}
            <section>
              <div className="mb-3 flex items-center justify-between px-1">
                <SkeletonBox className="h-4 w-32" />
                <SkeletonBox className="h-4 w-16 bg-info/30" />
              </div>
              <div className="overflow-hidden rounded-3xl border border-dark/5 bg-card shadow-card">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div
                    key={i}
                    className={`grid grid-cols-4 gap-3 px-6 py-4 ${i > 0 ? 'border-t-2 border-dark/10' : ''}`}
                  >
                    <SkeletonBox className="h-4 w-16" />
                    <SkeletonBox tone="prominent" className="h-4 w-28" />
                    <SkeletonBox className="h-4 w-20" />
                    <SkeletonBox className="h-4 w-24 justify-self-end" />
                  </div>
                ))}
              </div>
            </section>
          </div>
        </main>
      </div>

      {/* Mobile bottom tab bar — mirrors AppShell grid-cols-5 */}
      <nav className="fixed inset-x-0 bottom-0 z-30 border-t border-dark/5 bg-white md:hidden">
        <div className="grid grid-cols-5">
          {Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className="flex flex-col items-center justify-center gap-1 py-2"
            >
              <SkeletonBox className="h-5 w-5" />
              <SkeletonBox className="h-2 w-12" />
            </div>
          ))}
        </div>
      </nav>
    </div>
  );
}
